/* import __COLOCATED_TEMPLATE__ from './handoff-to-external-ticket-on-resolution.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type {
  HandoffOption,
  HandoffOptionGroup,
} from 'embercom/components/workflows/graph-editor/node-items/steps/handoff-to-salesforce-case';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type { SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import {
  StandalonePlatformToIcon,
  type StandalonePlatform,
} from 'embercom/objects/standalone/constants';
import { isPresent } from '@ember/utils';
import { action } from '@ember/object';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { capitalize } from '@ember/string';
import { tracked } from '@glimmer/tracking';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    step: $TSFixMe;
    isReadOnly: boolean;
    editorState: EditorState;
  };
}

export default class HandoffToExternalTicketOnResolution extends Component<Signature> {
  @service declare appService: { app: $TSFixMe };
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @tracked showConfigSheet = false;

  get platformType(): string | undefined {
    return this.appService.app.standalone_platforms?.firstObject?.platform_type;
  }

  get platformName(): string {
    return this.platformType ? capitalize(this.platformType) : '';
  }

  get platformIcon(): InterfaceIconName {
    return (
      (this.platformType && StandalonePlatformToIcon[this.platformType as StandalonePlatform]) ||
      'ticket'
    );
  }

  get platformTicketObjectName(): string {
    return this.platformType === 'salesforce'
      ? this.intl.t(
          'operator.workflows.visual-builder.handoff-to-external-ticket-on-resolution.salesforce.ticket-object-name',
        )
      : this.intl.t(
          'operator.workflows.visual-builder.handoff-to-external-ticket-on-resolution.platform-ticket-object-name',
        );
  }

  get isToggleDisabled(): boolean {
    if (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.salesforceConfig.isPendingSetup
    ) {
      return true;
    }

    return this.args.isReadOnly;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get handoffEnabled(): boolean {
    return Boolean(this.args.step.handoffToExternalTicketOnResolutionData?.handoffEnabled);
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.finStandaloneService.salesforceSetupData;
  }

  get salesforceHandoverOptions(): HandoffOptionGroup[] {
    let admins = this.salesforceSetupData.admins?.map((admin) => ({
      text: admin.name,
      value: admin.id,
    }));
    let queues = this.salesforceSetupData.queues?.map((queue) => ({
      text: queue.name,
      value: queue.id,
    }));

    let options = [];

    if (isPresent(queues)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.queues-title',
        ),
        items: queues,
      });
    }

    if (isPresent(admins)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.users-title',
        ),
        items: admins,
      });
    }

    if (options.length === 0) {
      return [
        {
          heading: '',
          items: [
            {
              text: this.intl.t(
                'operator.workflows.visual-builder.handoff-to-external-ticket-on-resolution.salesforce.no-queues-or-users-available',
              ),
              value: '',
              isDisabled: true,
            },
          ],
        },
      ];
    }

    return options;
  }

  get selectedHandoverOption(): HandoffOption | undefined {
    let selectedHandoverOption = this.salesforceHandoverOptions
      .flatMap((group) => group.items)
      .find(
        (option) =>
          option.value ===
          this.args.step.handoffToExternalTicketOnResolutionData?.handoffToSalesforceOwnerId,
      );
    return selectedHandoverOption;
  }

  private maybeInitHandoffDataFragment() {
    this.args.step.handoffToExternalTicketOnResolutionData ??= this.args.step.store.createFragment(
      'operator/fin/handoff-to-external-ticket-on-resolution-data',
    );
  }

  @action
  toggleConfigSheet() {
    this.showConfigSheet = !this.showConfigSheet;
  }

  @action
  setHandoffToSalesforceOwnerId(handoffId: string) {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.handoffToSalesforceOwnerId = handoffId;
  }

  @action
  toggleHandoffToExternalTicketOnResolution() {
    this.maybeInitHandoffDataFragment();
    this.args.step.handoffToExternalTicketOnResolutionData.handoffEnabled =
      !this.args.step.handoffToExternalTicketOnResolutionData.handoffEnabled;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::HandoffToExternalTicketOnResolution': typeof HandoffToExternalTicketOnResolution;
    'workflows/graph-editor/node-items/steps/fin/sections/handoff-to-external-ticket-on-resolution': typeof HandoffToExternalTicketOnResolution;
  }
}
